import React, { useState, useEffect, useCallback } from 'react';
import { api, formatINR, formatDate, toInputDate } from '../utils/api';
import './Subscriptions.css';

const TYPES   = ['All', 'Domain', 'Hosting', 'Mail', 'Other'];
const CYCLES  = ['Monthly', 'Quarterly', 'Annual', 'Custom'];
const PERIODS = [1, 2, 3, 5, 10];

const ACC_KEY = 'sub_accordion_v1';
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Client-side preview only — the server computes the authoritative value on save.
function previewNextRenewal(dateStr, years) {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr.slice(0, 10) + 'T00:00:00Z');
  d.setUTCFullYear(d.getUTCFullYear() + (parseInt(years, 10) || 1));
  return d.toISOString().slice(0, 10);
}

// Local-time YYYY-MM-DD (avoids the UTC off-by-one for IST mornings)
function localIso(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return d.getFullYear() + '-' + m + '-' + day;
}

function daysSince(dateStr, todayIso) {
  if (!dateStr) return null;
  const a = Date.UTC(+dateStr.slice(0, 4), +dateStr.slice(5, 7) - 1, +dateStr.slice(8, 10));
  const b = Date.UTC(+todayIso.slice(0, 4), +todayIso.slice(5, 7) - 1, +todayIso.slice(8, 10));
  return Math.round((b - a) / 86400000);
}

function sumBy(list, key) {
  return list.reduce((a, s) => a + (s[key] || 0), 0);
}

function dueLabel(days) {
  if (days < 0)   return Math.abs(days) + 'd overdue';
  if (days === 0) return 'Today';
  return 'in ' + days + 'd';
}

/* ---- One table of subscription rows. mode: 'pay' | 'collect' | 'upcoming' ---- */
function SubTable({ rows, mode, onToggle, onHistory, onEdit }) {
  return (
    <div className="table-wrap">
      <table className="sub-tbl">
        <thead>
          <tr>
            <th>Subscription</th>
            <th>Client</th>
            <th>Type</th>
            <th>{mode === 'collect' ? 'Paid On' : 'Renewal Date'}</th>
            <th>{mode === 'collect' ? 'Waiting' : 'Due'}</th>
            <th className="num">Our Cost</th>
            <th className="num">Client Cost</th>
            <th style={{textAlign:'center'}}>Action</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(s => (
            <SubRows key={s.id} s={s} mode={mode} onToggle={onToggle} onHistory={onHistory} onEdit={onEdit} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubRows({ s, mode, onToggle, onHistory, onEdit }) {
  const tone = mode === 'upcoming' ? 'ok' : s._tone;
  const showNote = mode === 'collect' && !!s.needs_reconciliation && !s.we_paid;
  const canUndoCollected = mode !== 'collect' && s.last_event_type === 'collected';
  const canUndoPaid = mode === 'collect' && !!s.we_paid;

  return (
    <React.Fragment>
      <tr className={'sub-row row-' + tone}>
        <td>
          <div className="sub-name">
            {s.name}
            {!!s.needs_reconciliation && (
              <span className="reconcile-badge" title="Renewed without the previous cycle's payment being marked Collected"
                onClick={() => onHistory(s)}>
                ⚠ Reconcile
              </span>
            )}
          </div>
          {s.platform && <div className="sub-platform">{s.platform}</div>}
        </td>
        <td style={{fontSize:11, color:'var(--text-mid)'}}>{s.client_name}</td>
        <td><span className={'badge sub-type-' + s.type.toLowerCase()}>{s.type}</span></td>
        <td>
          {mode === 'collect' ? (
            <div className="sub-date">{s._paidDate ? formatDate(s._paidDate) : '—'}</div>
          ) : (
            <React.Fragment>
              <div className="sub-date">{formatDate(s.renewal_date)}</div>
              <div className="sub-period">{s.renewal_period_years || 1} yr renewal</div>
            </React.Fragment>
          )}
        </td>
        <td>
          {mode === 'collect' ? (
            s._waited === null
              ? <span className="sub-muted">—</span>
              : <span className={'badge sub-days-chip chip-' + s._tone}>{s._waited + (s._waited === 1 ? ' day' : ' days')}</span>
          ) : mode === 'pay' ? (
            <span className={'badge sub-days-chip chip-' + s._tone}>{dueLabel(s.days_until_renewal)}</span>
          ) : (
            <span className="sub-muted">in {s.days_until_renewal} days</span>
          )}
        </td>
        <td className="num" style={{fontSize:12}}>{formatINR(s.our_cost)}</td>
        <td className="num">
          <div style={{fontSize:12, fontWeight:600}}>{formatINR(s.client_cost)}</div>
          <div style={{fontSize:10, color:'var(--text-light)'}}>+{formatINR(s.client_gst)} GST</div>
        </td>
        <td style={{textAlign:'center'}}>
          {mode === 'pay' && (
            <button className="sub-action sub-action-pay" onClick={() => onToggle(s, 'paid')}>Mark Paid</button>
          )}
          {mode === 'collect' && (
            <button className="sub-action sub-action-collect" onClick={() => onToggle(s, 'collected')}>Mark Collected</button>
          )}
          {mode === 'upcoming' && <span className="sub-muted">Not due yet</span>}
          {canUndoPaid && (
            <div><button className="sub-undo" onClick={() => onToggle(s, 'paid')}>Undo paid</button></div>
          )}
          {canUndoCollected && (
            <div><button className="sub-undo" onClick={() => onToggle(s, 'collected')}>Undo collected</button></div>
          )}
        </td>
        <td style={{whiteSpace:'nowrap'}}>
          <button className="icon-btn" onClick={() => onHistory(s)} title="View history" style={{marginRight:4}}>👁</button>
          <button className="icon-btn edit" onClick={() => onEdit(s)} title="Edit">✎</button>
        </td>
      </tr>
      {showNote && (
        <tr className={'sub-note-row row-' + tone}>
          <td colSpan={9}>
            Next payment due <strong>{formatDate(s.renewal_date)}</strong>, not yet paid. Mark the old cycle Collected to clear the reconcile flag.
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

export default function Subscriptions() {
  const [subs, setSubs]               = useState([]);
  const [clients, setClients]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [typeFilter, setTypeFilter]   = useState('All');
  const [search, setSearch]           = useState('');
  const [showAdd, setShowAdd]         = useState(false);
  const [editSub, setEditSub]         = useState(null);
  const [historySub, setHistorySub]   = useState(null);
  const [toggleModal, setToggleModal] = useState(null);
  const [showMigration, setShowMigration] = useState(false);

  // Accordion open/closed state, remembered between visits. Anything not yet
  // touched falls back to the default passed at the call site.
  const [acc, setAcc] = useState(function () {
    try { return JSON.parse(localStorage.getItem(ACC_KEY)) || {}; } catch (e) { return {}; }
  });

  function isOpen(key, def) {
    return Object.prototype.hasOwnProperty.call(acc, key) ? acc[key] : def;
  }

  function setAccValue(key, valueOrFn, def) {
    setAcc(function (prev) {
      const cur = Object.prototype.hasOwnProperty.call(prev, key) ? prev[key] : def;
      const val = typeof valueOrFn === 'function' ? valueOrFn(cur) : valueOrFn;
      const next = Object.assign({}, prev);
      next[key] = val;
      try { localStorage.setItem(ACC_KEY, JSON.stringify(next)); } catch (e) { /* storage unavailable */ }
      return next;
    });
  }

  function toggleAcc(key, def) {
    setAccValue(key, function (cur) { return !cur; }, def);
  }

  function jumpTo(secKey, elId) {
    setAccValue(secKey, true, true);
    setTimeout(function () {
      const el = document.getElementById(elId);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get('/subscriptions'), api.get('/clients')])
      .then(([s, c]) => { setSubs(s); setClients(c); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = subs.filter(s => {
    const matchType   = typeFilter === 'All' || s.type === typeFilter;
    const matchSearch = !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.client_name.toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  // KPIs — Annual Client Billing now ex-GST (fix #2)
  const totalCost     = subs.reduce((a, s) => a + s.our_cost, 0);
  const totalBilling  = subs.reduce((a, s) => a + s.client_cost, 0);
  const paidPending   = subs.filter(s => s.we_paid && !s.collected);
  const pendingAmt    = paidPending.reduce((a, s) => a + s.client_total, 0);
  const needsReconcile = subs.filter(s => s.needs_reconciliation);

  // ---- Sort every subscription into exactly one of three sections ----
  const todayIso  = localIso(new Date());
  const monthKey  = todayIso.slice(0, 7);
  const thisYear  = todayIso.slice(0, 4);

  const yetToPay = [];
  const awaiting = [];
  const upcoming = [];

  filtered.forEach(s => {
    const isAwaiting = (s.we_paid && !s.collected) || !!s.needs_reconciliation;
    if (isAwaiting) {
      const paidRaw  = s.we_paid ? (s.we_paid_date || s.last_paid_date) : s.last_paid_date;
      const paidDate = paidRaw ? String(paidRaw).slice(0, 10) : null;
      const waited   = daysSince(paidDate, todayIso);
      const tone     = (s.needs_reconciliation || (waited !== null && waited >= 30)) ? 'red' : 'amber';
      awaiting.push(Object.assign({}, s, { _paidDate: paidDate, _waited: waited, _tone: tone }));
      return;
    }
    const days    = s.days_until_renewal;
    const inMonth = (s.renewal_date || '').slice(0, 7) === monthKey;
    if (days <= 60 || inMonth) {
      const tone = (days <= 30 || inMonth) ? 'red' : 'amber';
      yetToPay.push(Object.assign({}, s, { _tone: tone }));
    } else {
      upcoming.push(s);
    }
  });

  awaiting.sort((a, b) => {
    if (!!b.needs_reconciliation !== !!a.needs_reconciliation) return b.needs_reconciliation ? 1 : -1;
    return (b._waited === null ? -1 : b._waited) - (a._waited === null ? -1 : a._waited);
  });

  const payRed    = yetToPay.filter(s => s._tone === 'red');
  const payAmber  = yetToPay.filter(s => s._tone === 'amber');
  const awaitRed   = awaiting.filter(s => s._tone === 'red');
  const awaitAmber = awaiting.filter(s => s._tone === 'amber');

  // Upcoming: year -> month -> rows
  const yearMap = {};
  upcoming.forEach(s => {
    const y = s.renewal_date.slice(0, 4);
    const m = s.renewal_date.slice(5, 7);
    if (!yearMap[y]) yearMap[y] = {};
    if (!yearMap[y][m]) yearMap[y][m] = [];
    yearMap[y][m].push(s);
  });
  const years = Object.keys(yearMap).sort();

  const yetTotal   = sumBy(yetToPay, 'our_cost');
  const awaitTotal = sumBy(awaiting, 'client_total');
  const upTotal    = sumBy(upcoming, 'client_cost');

  function openToggle(sub, field) {
    // Paid: undo is offered as long as We Paid is currently set.
    // Collected: the flags snap back to Pending the instant Collected is confirmed
    // (that's the new renewal flow), so "is this an undo?" can't be read from the
    // live collected value — it's read from whether the most recent logged event
    // for this subscription was that same Collected action.
    const isUndo = field === 'paid' ? sub.we_paid : sub.last_event_type === 'collected';
    setToggleModal({ sub, field, isUndo: !!isUndo });
  }

  // ---- Small render helpers (plain functions, not components, so state is kept) ----
  function accHead(key, def, title, count, amount, note, level) {
    const open = isOpen(key, def);
    return (
      <button type="button" className={'acc-head acc-' + level} aria-expanded={open}
        onClick={() => toggleAcc(key, def)}>
        <span className="acc-chev">{open ? '▾' : '▸'}</span>
        <span className="acc-title">{title}</span>
        {note && <span className="acc-note">{note}</span>}
        <span className="acc-spacer" />
        <span className="acc-count">{count}</span>
        <span className="acc-amt">{formatINR(amount)}</span>
      </button>
    );
  }

  function renderGroup(key, tone, title, list, mode, amountKey) {
    if (list.length === 0) return null;
    const open = isOpen(key, true);
    return (
      <div className={'acc-group acc-group-' + tone} key={key}>
        <button type="button" className={'acc-head acc-group-head acc-tone-' + tone} aria-expanded={open}
          onClick={() => toggleAcc(key, true)}>
          <span className="acc-chev">{open ? '▾' : '▸'}</span>
          <span className="acc-title">{title}</span>
          <span className="acc-spacer" />
          <span className="acc-count">{list.length}</span>
          <span className="acc-amt">{formatINR(sumBy(list, amountKey))}</span>
        </button>
        {open && (
          <SubTable rows={list} mode={mode} onToggle={openToggle} onHistory={setHistorySub} onEdit={setEditSub} />
        )}
      </div>
    );
  }

  const noMatch = filtered.length === 0;

  return (
    <div className="page-content">

      <div className="topbar">
        <div className="topbar-title">Subscriptions</div>
        <div className="topbar-right">
          <input className="sub-search" placeholder="Search client or subscription…"
            value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-outline" onClick={() => setShowMigration(true)} style={{fontSize:11}}>
            Legacy Data Migration
          </button>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Subscription</button>
        </div>
      </div>

      {needsReconcile.length > 0 && (
        <div className="reconcile-banner">
          ⚠ <strong>{needsReconcile.length}</strong> subscription{needsReconcile.length !== 1 ? 's' : ''} need{needsReconcile.length === 1 ? 's' : ''} reconciliation —
          renewed without the previous cycle's payment ever being marked Collected. Open a flagged row's history to settle it.
        </div>
      )}

      <div className="kpi-grid kpi-grid-4">
        <div className="kpi-card">
          <div className="kpi-label">Total Subscriptions</div>
          <div className="kpi-value">{subs.length}</div>
          <div className="kpi-sub">across <strong>{new Set(subs.map(s=>s.client_id)).size}</strong> clients</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Annual Cost to 8020</div>
          <div className="kpi-value red">{formatINR(totalCost)}</div>
          <div className="kpi-sub">incl. GST</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Annual Client Billing</div>
          <div className="kpi-value green">{formatINR(totalBilling)}</div>
          <div className="kpi-sub">ex-GST</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Paid — Pending Collection</div>
          <div className="kpi-value red">{formatINR(pendingAmt)}</div>
          <div className="kpi-sub"><strong>{paidPending.length}</strong> subscription{paidPending.length !== 1 ? 's' : ''}</div>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-3 sub-summary">
        <button type="button" className="kpi-card sub-sum-card sub-sum-red" onClick={() => jumpTo('sec1', 'sub-sec-1')}>
          <div className="kpi-label">Yet to be paid</div>
          <div className="kpi-value">{formatINR(yetTotal)}</div>
          <div className="kpi-sub"><strong>{yetToPay.length}</strong> due within 60 days · <strong>{payRed.length}</strong> red · <strong>{payAmber.length}</strong> amber</div>
        </button>
        <button type="button" className="kpi-card sub-sum-card sub-sum-amber" onClick={() => jumpTo('sec2', 'sub-sec-2')}>
          <div className="kpi-label">Awaiting from client</div>
          <div className="kpi-value">{formatINR(awaitTotal)}</div>
          <div className="kpi-sub"><strong>{awaiting.length}</strong> paid, not collected · <strong>{awaitRed.length}</strong> red · <strong>{awaitAmber.length}</strong> amber</div>
        </button>
        <button type="button" className="kpi-card sub-sum-card sub-sum-ok" onClick={() => jumpTo('sec3', 'sub-sec-3')}>
          <div className="kpi-label">Upcoming renewals</div>
          <div className="kpi-value">{formatINR(upTotal)}</div>
          <div className="kpi-sub"><strong>{upcoming.length}</strong> later than 60 days</div>
        </button>
      </div>

      <div className="card sub-filter-card">
        <div className="card-header">
          <span className="card-title">Subscriptions</span>
          <div className="filter-tabs">
            {TYPES.map(t => (
              <button key={t}
                className={'filter-tab ' + (typeFilter === t ? 'active' : '')}
                onClick={() => setTypeFilter(t)}>{t}</button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card"><div className="loading-state">Loading…</div></div>
      ) : noMatch ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">🔄</div>
            {subs.length === 0 ? 'No subscriptions yet. Add one above.' : 'No results for this filter.'}
          </div>
        </div>
      ) : (
        <React.Fragment>

          {/* 1. Yet to be paid */}
          <div className="card acc-card" id="sub-sec-1">
            {accHead('sec1', true, '1. Yet to be paid', yetToPay.length, yetTotal, 'we owe vendors', 'section')}
            {isOpen('sec1', true) && (
              yetToPay.length === 0
                ? <div className="acc-empty">Nothing due within the next 60 days.</div>
                : <div>
                    {renderGroup('g1red', 'red', 'Red: overdue, this month or within 30 days', payRed, 'pay', 'our_cost')}
                    {renderGroup('g1amb', 'amber', 'Amber: next 60 days', payAmber, 'pay', 'our_cost')}
                  </div>
            )}
          </div>

          {/* 2. Awaiting from client */}
          <div className="card acc-card" id="sub-sec-2">
            {accHead('sec2', true, '2. Awaiting from client', awaiting.length, awaitTotal, 'paid to vendor, client yet to pay', 'section')}
            {isOpen('sec2', true) && (
              awaiting.length === 0
                ? <div className="acc-empty">Nothing waiting on a client payment.</div>
                : <div>
                    {renderGroup('g2red', 'red', 'Red: waiting 30+ days, or reconcile', awaitRed, 'collect', 'client_total')}
                    {renderGroup('g2amb', 'amber', 'Amber: waiting under 30 days', awaitAmber, 'collect', 'client_total')}
                  </div>
            )}
          </div>

          {/* 3. Upcoming renewals */}
          <div className="card acc-card" id="sub-sec-3">
            {accHead('sec3', true, '3. Upcoming renewals', upcoming.length, upTotal, 'later than 60 days', 'section')}
            {isOpen('sec3', true) && (
              upcoming.length === 0
                ? <div className="acc-empty">No renewals beyond the next 60 days.</div>
                : <div>
                    {years.map(y => {
                      const yKey = 'y' + y;
                      const yRows = [].concat.apply([], Object.keys(yearMap[y]).map(m => yearMap[y][m]));
                      const yOpen = isOpen(yKey, y === thisYear);
                      return (
                        <div className="acc-year" key={y}>
                          {accHead(yKey, y === thisYear, y, yRows.length, sumBy(yRows, 'client_cost'), null, 'year')}
                          {yOpen && Object.keys(yearMap[y]).sort().map(m => {
                            const mKey = 'm' + y + '-' + m;
                            const mRows = yearMap[y][m];
                            const mOpen = isOpen(mKey, true);
                            return (
                              <div className="acc-month" key={m}>
                                <button type="button" className="acc-head acc-month-head" aria-expanded={mOpen}
                                  onClick={() => toggleAcc(mKey, true)}>
                                  <span className="acc-chev">{mOpen ? '▾' : '▸'}</span>
                                  <span className="acc-title">{MONTH_NAMES[parseInt(m, 10) - 1] + ' ' + y}</span>
                                  <span className="acc-spacer" />
                                  <span className="acc-count">{mRows.length}</span>
                                  <span className="acc-amt">{formatINR(sumBy(mRows, 'client_cost'))}</span>
                                </button>
                                {mOpen && (
                                  <SubTable rows={mRows} mode="upcoming" onToggle={openToggle} onHistory={setHistorySub} onEdit={setEditSub} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
            )}
          </div>

          <div className="sub-basis-note">
            Section totals: Yet to be paid shows our cost, Awaiting from client shows the client total (as in the Paid — Pending Collection card), Upcoming shows client cost.
          </div>
        </React.Fragment>
      )}

      {toggleModal && (
        <ToggleModal
          sub={toggleModal.sub}
          field={toggleModal.field}
          isUndo={toggleModal.isUndo}
          onClose={() => setToggleModal(null)}
          onSaved={() => { setToggleModal(null); load(); }}
        />
      )}

      {showAdd && (
        <SubFormModal
          clients={clients}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}

      {editSub && (
        <SubFormModal
          sub={editSub}
          clients={clients}
          onClose={() => setEditSub(null)}
          onSaved={() => { setEditSub(null); load(); }}
        />
      )}

      {historySub && (
        <HistoryModal
          sub={historySub}
          onClose={() => setHistorySub(null)}
        />
      )}

      {showMigration && (
        <LegacyMigrationModal
          onClose={() => setShowMigration(false)}
          onApplied={() => { setShowMigration(false); load(); }}
        />
      )}
    </div>
  );
}

/* ---- Toggle Modal — no outside-click close (fix #1) ---- */
function ToggleModal({ sub, field, isUndo, onClose, onSaved }) {
  const [date, setDate]           = useState(new Date().toISOString().split('T')[0]);
  const [reference, setReference] = useState('');
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);

  const isPaid      = field === 'paid';
  const actionLabel = isPaid ? 'Paid to Vendor' : 'Collected from Client';

  async function handleConfirm() {
    setSaving(true);
    try {
      const endpoint = '/subscriptions/' + sub.id + '/' + (isPaid ? 'paid' : 'collected');
      const body = isUndo
        ? { [isPaid ? 'paid' : 'collected']: false }
        : { [isPaid ? 'paid' : 'collected']: true, date, reference, notes };
      await api.patch(endpoint, body);
      onSaved();
    } catch(err) { alert(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">
            {isUndo ? 'Undo — Mark as Not ' + (isPaid ? 'Paid' : 'Collected') + '?' : 'Mark as ' + actionLabel}
          </div>
          <div className="modal-sub">{sub.name} · {sub.client_name}</div>
        </div>

        {!isUndo && (
          <div className="modal-body">
            {isPaid && (
              <div className="toggle-info">
                Renewal date will advance from <strong>{formatDate(sub.renewal_date)}</strong> to{' '}
                <strong>{formatDate(previewNextRenewal(sub.renewal_date, sub.renewal_period_years))}</strong>{' '}
                ({sub.renewal_period_years || 1} yr period). Collected will show Pending for the new cycle.
              </div>
            )}
            {!isPaid && (
              <div className="toggle-info">
                This closes out the cycle — We Paid and Collected will both reset to Pending, ready for the next renewal on <strong>{formatDate(sub.renewal_date)}</strong>.
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Reference / UTR (optional)</label>
              <input className="form-input" placeholder="e.g. UTR123456789" value={reference} onChange={e => setReference(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <input className="form-input" placeholder="Any notes…" value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>
        )}

        {isUndo && (
          <div className="modal-body">
            <div style={{fontSize:12, color:'var(--text-mid)'}}>
              This will remove the {isPaid ? 'payment' : 'collection'} record for this subscription cycle
              {isPaid ? ' and restore the renewal date to what it was before.' : ' and restore the We Paid state it closed out.'}
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            className="btn"
            style={{ background: isUndo ? 'var(--danger)' : isPaid ? 'var(--success)' : 'var(--info)', color:'#fff' }}
            onClick={handleConfirm}
            disabled={saving}
          >
            {saving ? 'Saving…' : isUndo ? 'Yes, Undo' : 'Confirm ' + actionLabel}
          </button>
        </div>
        <div className="modal-note">You can always undo this by clicking the toggle again.</div>
      </div>
    </div>
  );
}

/* ---- Add / Edit Subscription Modal — no outside-click close, with Archive (fix #1, #4) ---- */
function SubFormModal({ sub, clients, onClose, onSaved }) {
  const isEdit = !!sub;
  const [form, setForm] = useState({
    client_id:     sub?.client_id     || '',
    name:          sub?.name          || '',
    type:          sub?.type          || 'Domain',
    platform:      sub?.platform      || '',
    billing_cycle: sub?.billing_cycle || 'Annual',
    renewal_date:  toInputDate(sub?.renewal_date) || '',
    renewal_period_years: sub?.renewal_period_years || 1,
    our_cost:      sub?.our_cost      || '',
    client_cost:   sub?.client_cost   || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  function setF(k, v) { setForm(f => ({...f, [k]: v})); }

  async function handleSave() {
    if (!form.client_id)    { setError('Client is required.'); return; }
    if (!form.name)         { setError('Subscription name is required.'); return; }
    if (!form.renewal_date) { setError('Renewal date is required.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) {
        await api.put('/subscriptions/' + sub.id, form);
      } else {
        await api.post('/subscriptions', form);
      }
      onSaved();
    } catch(err) { setError(err.message); setSaving(false); }
  }

  async function handleArchive() {
    if (!window.confirm('Archive this subscription? It will be hidden from the list. Data is preserved (not deleted) and can be restored manually if needed.')) return;
    setSaving(true);
    try {
      await api.delete('/subscriptions/' + sub.id);
      onSaved();
    } catch(err) { setError(err.message); setSaving(false); }
  }

  const clientGst   = form.client_cost ? (parseFloat(form.client_cost) * 0.18).toFixed(0) : 0;
  const clientTotal = form.client_cost ? (parseFloat(form.client_cost) * 1.18).toFixed(0) : 0;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Subscription' : 'Add Subscription'}</div>
        </div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}

          <div className="form-row">
            <div className="form-group" style={{flex:2}}>
              <label className="form-label">Client *</label>
              <select className="form-select" value={form.client_id} onChange={e => setF('client_id', e.target.value)}>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{flex:1}}>
              <label className="form-label">Type</label>
              <select className="form-select" value={form.type} onChange={e => setF('type', e.target.value)}>
                {['Domain','Hosting','Mail','Other'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Subscription Name *</label>
            <input className="form-input" value={form.name} onChange={e => setF('name', e.target.value)}
              placeholder="e.g. accenture-india.com or Google Workspace" autoFocus={!isEdit} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Platform</label>
              <input className="form-input" value={form.platform} onChange={e => setF('platform', e.target.value)}
                placeholder="GoDaddy, Wix, Mitsu…" />
            </div>
            <div className="form-group">
              <label className="form-label">Billing Cycle</label>
              <select className="form-select" value={form.billing_cycle} onChange={e => setF('billing_cycle', e.target.value)}>
                {CYCLES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Next Renewal Date *</label>
              <input className="form-input" type="date" value={form.renewal_date} onChange={e => setF('renewal_date', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Renewal Period</label>
              <select className="form-select" value={form.renewal_period_years} onChange={e => setF('renewal_period_years', parseInt(e.target.value, 10))}>
                {PERIODS.map(p => <option key={p} value={p}>{p} {p === 1 ? 'Year' : 'Years'}</option>)}
              </select>
            </div>
            <div className="form-group" style={{flex:2}}>
              <div className="toggle-info" style={{marginBottom:0}}>
                Marking <strong>We Paid</strong> advances the renewal date forward by this period automatically. Set this to how long the vendor actually renews for (e.g. 2-year domain registration).
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Our Cost (₹ incl. GST)</label>
              <input className="form-input" type="number" value={form.our_cost}
                onChange={e => setF('our_cost', e.target.value)} placeholder="Amount we pay" />
            </div>
            <div className="form-group">
              <label className="form-label">Client Cost (₹ ex-GST)</label>
              <input className="form-input" type="number" value={form.client_cost}
                onChange={e => setF('client_cost', e.target.value)} placeholder="Amount we charge" />
            </div>
          </div>

          {form.client_cost > 0 && (
            <div className="sub-cost-preview">
              + GST (18%): <strong>{formatINR(clientGst)}</strong> &nbsp;=&nbsp;
              Client pays: <strong>{formatINR(clientTotal)}</strong>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{justifyContent:'space-between'}}>
          <div>
            {isEdit && (
              <button
                onClick={handleArchive}
                disabled={saving}
                style={{
                  background:'transparent', border:'1px solid var(--danger, #c0392b)',
                  color:'var(--danger, #c0392b)', padding:'8px 14px',
                  borderRadius:6, cursor:'pointer', fontSize:12
                }}
              >
                Archive
              </button>
            )}
          </div>
          <div>
            <button className="btn btn-outline" onClick={onClose} style={{marginRight:8}}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Subscription'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- History Modal — new component (fix #5) ---- */
function HistoryModal({ sub, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    api.get('/subscriptions/' + sub.id + '/history')
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [sub.id]);

  function eventLabel(type) {
    const labels = {
      'created':                     'Created',
      'paid':                        'Paid to Vendor',
      'unpaid':                      'Reversed: Not Paid',
      'collected':                   'Collected from Client',
      'uncollected':                 'Reversed: Not Collected',
      'renewed':                     'Renewed (new cycle)',
      'auto_reset_pending_collection': '⚠ Auto-reset — never reconciled',
      'migrated':                    'Migrated to renewal-period system',
      'migration_anomaly':           '⚠ Migration — needs manual review'
    };
    return labels[type] || type;
  }

  function eventColor(type) {
    if (type === 'paid' || type === 'collected') return 'var(--success, #27ae60)';
    if (type === 'unpaid' || type === 'uncollected') return 'var(--danger, #c0392b)';
    if (type === 'renewed') return 'var(--info, #2980b9)';
    if (type === 'auto_reset_pending_collection' || type === 'migration_anomaly') return 'var(--warning, #b58900)';
    if (type === 'migrated') return 'var(--info, #2980b9)';
    return 'var(--text-mid, #666)';
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:600}}>
        <div className="modal-header">
          <div className="modal-title">History — {sub.name}</div>
          <div className="modal-sub">{sub.client_name} · {sub.type} · {sub.billing_cycle}</div>
        </div>

        <div className="modal-body" style={{maxHeight:500, overflowY:'auto'}}>
          {/* Subscription details summary */}
          <div style={{background:'var(--bg-soft, #f7f7f8)', padding:12, borderRadius:6, marginBottom:16, fontSize:12}}>
            <div style={{fontWeight:600, marginBottom:8, fontSize:13}}>Subscription Details</div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:6}}>
              <div><strong>Platform:</strong> {sub.platform || '—'}</div>
              <div><strong>Current Renewal:</strong> {formatDate(sub.renewal_date)}</div>
              <div><strong>Our Cost:</strong> {formatINR(sub.our_cost)}</div>
              <div><strong>Client Cost:</strong> {formatINR(sub.client_cost)} ex-GST</div>
              <div style={{gridColumn:'1/3'}}><strong>Client Total:</strong> {formatINR(sub.client_total)} (incl. {formatINR(sub.client_gst)} GST)</div>
            </div>
          </div>

          {/* Activity timeline */}
          <div style={{fontWeight:600, marginBottom:8, fontSize:13}}>Activity Log</div>
          {loading ? (
            <div className="loading-state">Loading history…</div>
          ) : error ? (
            <div className="setup-error">{error}</div>
          ) : !data || data.history.length === 0 ? (
            <div style={{padding:16, textAlign:'center', color:'var(--text-light)', fontSize:12}}>
              No activity yet for this subscription.
            </div>
          ) : (
            <div>
              {data.history.map(h => (
                <div key={h.id} style={{
                  borderLeft: '3px solid ' + eventColor(h.event_type),
                  paddingLeft: 12, marginBottom: 12, fontSize: 12
                }}>
                  <div style={{fontWeight: 600, color: eventColor(h.event_type)}}>
                    {eventLabel(h.event_type)}
                  </div>
                  {h.event_date && (
                    <div style={{color: 'var(--text-mid, #666)'}}>
                      Date: <strong>{formatDate(h.event_date)}</strong>
                    </div>
                  )}
                  <div style={{color: 'var(--text-light, #999)', fontSize: 11}}>
                    Recorded: {new Date(h.recorded_at).toLocaleString()}
                  </div>
                  {h.renewal_date_at_event && (
                    <div style={{color: 'var(--text-light, #999)', fontSize: 11}}>
                      Cycle ending: {formatDate(h.renewal_date_at_event)}
                    </div>
                  )}
                  {h.reference && (
                    <div style={{marginTop: 4}}>
                      <strong>Ref:</strong> {h.reference}
                    </div>
                  )}
                  {h.notes && (
                    <div style={{marginTop: 4, fontStyle: 'italic', color: 'var(--text-mid, #666)'}}>
                      {h.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Legacy Migration Modal — one-time backfill for subscriptions created before
   the renewal-period system existed. Preview first, apply only on explicit confirm. ---- */
function LegacyMigrationModal({ onClose, onApplied }) {
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [preview, setPreview]     = useState(null);
  const [applying, setApplying]   = useState(false);
  const [applied, setApplied]     = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    api.get('/subscriptions/legacy-migration/preview')
      .then(d => { setPreview(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  function caseLabel(c) {
    const labels = {
      fully_reconciled_advance:  'Paid & Collected — advance + reset',
      paid_uncollected_flagged:  'Paid, never Collected — advance + flag for reconciliation',
      paid_uncollected_carried:  'Paid, never Collected — advance, still outstanding (not urgent)',
      anomaly_needs_review:      '⚠ Inconsistent — flagged for manual review',
      no_change:                 'Already clean — no change'
    };
    return labels[c] || c;
  }

  function caseColor(c) {
    if (c === 'no_change') return 'var(--text-light, #999)';
    if (c === 'anomaly_needs_review' || c === 'paid_uncollected_flagged') return 'var(--danger, #c0392b)';
    return 'var(--info, #2980b9)';
  }

  async function handleApply() {
    setApplying(true);
    try {
      const result = await api.post('/subscriptions/legacy-migration/apply', {});
      setApplied(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  }

  const changedRows = preview ? preview.plan.filter(p => p.case !== 'no_change') : [];

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:760}}>
        <div className="modal-header">
          <div className="modal-title">Legacy Subscription Data Migration</div>
          <div className="modal-sub">One-time backfill to bring existing subscriptions onto the new renewal-period system</div>
        </div>

        <div className="modal-body" style={{maxHeight:520, overflowY:'auto'}}>
          {loading ? (
            <div className="loading-state">Checking existing records…</div>
          ) : error ? (
            <div className="setup-error">{error}</div>
          ) : applied ? (
            <div>
              <div className="toggle-info" style={{background:'var(--success-bg, #eafaf1)', color:'var(--success, #27ae60)'}}>
                ✓ Migration applied. {applied.changed} subscription{applied.changed !== 1 ? 's' : ''} updated, {applied.unchanged} left unchanged.
                A backup was taken first ({applied.backup && applied.backup.files && applied.backup.files.join(', ')}).
              </div>
            </div>
          ) : (
            <div>
              {preview.already_applied && (
                <div className="toggle-info" style={{background:'var(--warning-bg)', color:'var(--warning)'}}>
                  This migration has already been applied once. Running it again is blocked — the list below is just a live preview of current state.
                </div>
              )}
              <div className="toggle-info">
                Every active subscription is checked against its current We Paid / Collected flags. {changedRows.length} of {preview.plan.length}
                {' '}need a change. Nothing is written until you click Apply below, and a full backup is taken automatically first.
              </div>

              <table style={{width:'100%', fontSize:11, borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{textAlign:'left', borderBottom:'1px solid var(--border-light)'}}>
                    <th style={{padding:'4px 6px'}}>Subscription</th>
                    <th style={{padding:'4px 6px'}}>Before</th>
                    <th style={{padding:'4px 6px'}}>After</th>
                    <th style={{padding:'4px 6px'}}>What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.plan.map(p => (
                    <tr key={p.id} style={{borderBottom:'1px solid var(--border-light)'}}>
                      <td style={{padding:'4px 6px', fontWeight:600}}>{p.name}</td>
                      <td style={{padding:'4px 6px'}}>
                        {formatDate(p.before.renewal_date)}<br/>
                        {p.before.we_paid ? 'Paid' : 'Not Paid'} / {p.before.collected ? 'Collected' : 'Pending'}
                      </td>
                      <td style={{padding:'4px 6px'}}>
                        {formatDate(p.after.renewal_date)}<br/>
                        {p.after.we_paid ? 'Paid' : 'Not Paid'} / {p.after.collected ? 'Collected' : 'Pending'}
                        {!!p.after.needs_reconciliation && <span className="reconcile-badge" style={{marginLeft:4}}>⚠</span>}
                      </td>
                      <td style={{padding:'4px 6px', color:caseColor(p.case)}}>{caseLabel(p.case)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {!preview.already_applied && changedRows.length > 0 && !confirmStep && (
                <div style={{marginTop:12}}>
                  <button className="btn btn-primary" onClick={() => setConfirmStep(true)}>
                    Review complete — proceed to Apply
                  </button>
                </div>
              )}

              {confirmStep && !preview.already_applied && (
                <div className="toggle-info" style={{marginTop:12, background:'var(--danger-bg, #fbe6e4)', color:'var(--danger, #c0392b)'}}>
                  This will update {changedRows.length} subscription{changedRows.length !== 1 ? 's' : ''} in the live database, after taking a fresh backup.
                  This action runs once and can't be re-run. Confirm you've reviewed the table above.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>{applied ? 'Close' : 'Cancel'}</button>
          {!applied && confirmStep && !preview?.already_applied && (
            <button className="btn" style={{background:'var(--danger, #c0392b)', color:'#fff'}} onClick={handleApply} disabled={applying}>
              {applying ? 'Applying…' : 'Apply Migration Now'}
            </button>
          )}
          {applied && (
            <button className="btn btn-primary" onClick={onApplied}>Done — Refresh List</button>
          )}
        </div>
      </div>
    </div>
  );
}